import { Box, Grid, Typography, styled, useTheme } from '@mui/material'
import { ACTION_TYPES } from 'packages/core'
import { Divider, StatusIndicator } from 'packages/eid-ui'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CSA_FIELD_TYPES_SELECTION_RULE_TYPE } from 'utils'

const ExpandButton = styled(Box)(() => ({
    textTransform: 'uppercase',
    zIndex: 9999,
    marginTop: '0.2rem',
    color: '#307fc1',
    fontSize: '1.2rem',
    '&:hover': {
        cursor: 'pointer',
    },
}))

const ExpandIcon = styled(Box)(({ theme }) => ({
    width: '1.8rem',
    height: '1.8rem',
    marginLeft: '0.8rem',
    marginTop: '-0.2rem',
    borderRadius: '50%',
    border: `0.1rem solid ${theme?.palette?.primary?.main}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme?.palette?.primary?.main,
    lineHeight: '0',
    fontSize: '1.5rem',
}))

const Attribute = ({ label, value, ...rest }) => {
    const theme = useTheme()

    return (
        <Box {...rest}>
            <Box
                color={theme?.palette?.grey?.[900]}
                fontSize="1.2rem"
                style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}
            >
                {label}
            </Box>
            <Box fontSize="1.4rem">{value}</Box>
        </Box>
    )
}

const CSAAttributeFieldTypes = ({ fieldTypeValueJson }) => {
    const [showMore, setShowMore] = useState(false)
    const { t } = useTranslation()

    const bindFieldTypes = () => {
        return (
            <Fragment>
                <Grid
                    item
                    xs={12}
                    style={{
                        marginBottom: '1rem',
                    }}
                >
                    {fieldTypeValueJson?.length &&
                        fieldTypeValueJson.map((field, index) => {
                            return (
                                <DynamicFieldAdditionalLabelValue
                                    item={field}
                                    key={`field-type-${index}`}
                                />
                            )
                        })}
                </Grid>
            </Fragment>
        )
    }
    return (
        <>
            <Divider />

            <Box
                sx={(theme) => ({
                    padding: `${'0 1.6rem'}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: '0.1rem solid #d8d8d8 !important',
                    width: '100% !important',
                    '& div': {
                        fontSize: '1.8rem',
                    },
                    background: `linear-gradient(90deg, rgba(246,246,248,1) 0%, rgba(244,244,246,1) 0%, rgba(238,103,35,1) 0%, rgba(23,154,170,0.95) 0%, rgba(23,129,188,0.95) 100%),repeating-linear-gradient(${theme?.palette?.common?.white}, ${theme?.palette?.common?.white} 3px, rgba(0, 0, 0, 0.02) 3px, rgba(0, 0, 0, 1.02) 4px)`,
                    '& div': {
                        fontSize: '1.4rem',
                        color: `${theme?.palette?.common?.white} !important`,
                        borderColor: `${theme?.palette?.common?.white} !important`,
                    },
                    '& span': {
                        color: `${theme?.palette?.common?.white} !important`,
                    },
                })}
            >
                <HorizontalOrVertical
                    padding={'1.6rem 0'}
                    label={t('Common_SelectedFieldTypeValues')}
                />
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    fontSize="1.2rem"
                    fontWeight="bold"
                >
                    <ExpandButton
                        component="span"
                        display="flex"
                        onClick={() => setShowMore(!showMore)}
                    >
                        {showMore ? t('Hide') : t('Show')}
                        <ExpandIcon>{showMore ? '-' : '+'}</ExpandIcon>
                    </ExpandButton>
                </Box>
            </Box>
            {showMore && (
                <Box display="flex" marginTop="1.2rem">
                    <Grid container spacing={1}>
                        {bindFieldTypes()}
                    </Grid>
                </Box>
            )}
        </>
    )
}

export default CSAAttributeFieldTypes

const HorizontalOrVertical = styled(Attribute)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '1.6rem 0',
    '& div': {
        fontWeight: '600',
        color: theme?.palette?.common?.black,
    },

    '& > div:last-child': {
        marginLeft: '1.6rem',
    },

    '@media (max-width:660px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        '& > div:last-child': {
            marginLeft: 0,
        },
    },
}))

const DynamicFieldAdditionalLabelValue = ({ item }) => {
    const { t } = useTranslation()

    const getChangeLabel = (fieldValue) => {
        if (Array.isArray(fieldValue) && fieldValue?.length) {
            const isAdded = fieldValue.find(
                (x) => x.actionType === ACTION_TYPES.ADDED,
            )
            const isDeleted = fieldValue.find(
                (x) => x.actionType === ACTION_TYPES.DELETED,
            )
            const isUpdated = fieldValue.find(
                (x) => x.actionType === ACTION_TYPES.UPDATED,
            )
            const isModified = (isAdded && isDeleted) || isUpdated
            if (isModified) {
                return 'Common_Updated'
            }
            return isAdded ? 'Common_Added' : isDeleted ? 'Common_Deleted' : ''
        }
        return 'Common_NoChanged'
    }

    const getChangeLabelVariant = (fieldValue) => {
        if (Array.isArray(fieldValue) && fieldValue?.length) {
            const isAdded = fieldValue.find(
                (x) => x.actionType === ACTION_TYPES.ADDED,
            )
            const isDeleted = fieldValue.find(
                (x) => x.actionType === ACTION_TYPES.DELETED,
            )
            const isUpdated = fieldValue.find(
                (x) => x.actionType === ACTION_TYPES.UPDATED,
            )
            const isModified = (isAdded && isDeleted) || isUpdated
            if (isModified) {
                return 'Open_PendingApproval'
            }
            return isAdded ? 'Approved' : isDeleted ? 'Rejected' : ''
        }
    }

    const groupByActionType = (data) => {
        if (
            item?.selectionRuleTypeID !==
            CSA_FIELD_TYPES_SELECTION_RULE_TYPE.MULTI_SELECT
        ) {
            return []
        }
        return data.reduce((acc, item) => {
            acc[item.actionType] = acc[item.actionType] || []
            acc[item.actionType].push(item)
            return acc
        }, {})
    }
    const groupedData = groupByActionType(item?.multiValueAssignments)

    return (
        <Box
            sx={{
                width: '100% !important',
                display: 'flex',
                flexDirection: 'column',
                padding: `${'0 1.6rem'}`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    borderBottom: '0.1rem solid #efeff1',
                    alignItems: 'center',
                    paddingBottom: '0.7rem',
                    paddingTop: '.7rem',
                    gap: '2rem',
                }}
            >
                <Typography
                    sx={{
                        color: '#919193',
                        fontSize: '1.2rem',
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                    }}
                >
                    {t('Common_FieldTypeValues')}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    paddingTop: '1.2rem',
                }}
            >
                {/* Values Section */}
                <Box>
                    {item?.selectionRuleTypeID ===
                    CSA_FIELD_TYPES_SELECTION_RULE_TYPE.MULTI_SELECT ? (
                        <>
                            {Object.keys(groupedData).map((actionType) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: '1.2rem',
                                        alignItems: 'flex-start',
                                        paddingBottom: '0.8rem',
                                        width: '30rem',
                                    }}
                                >
                                    <Box sx={{ width: '8rem' }}>
                                        <Typography
                                            sx={{
                                                lineHeight: '1.1',
                                                color: '#919193',
                                                fontSize: '1.2rem',
                                                textTransform: 'uppercase',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {actionType}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {groupedData[actionType].map(
                                            (item, index) => (
                                                <Typography
                                                    key={`value-items-cart-${index}`}
                                                    style={{
                                                        fontSize: '1.4rem',
                                                        color: '#000',
                                                        marginLeft: '0.8rem',
                                                        fontWeight: '500',
                                                        lineHeight: '1.1',
                                                        paddingBottom: '0.8rem',
                                                    }}
                                                >
                                                    {item?.valueName}
                                                </Typography>
                                            ),
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '1.2rem',
                                    alignItems: 'flex-start',
                                    paddingBottom: '0.8rem',
                                    width: '30rem',
                                }}
                            >
                                <Box sx={{ width: '8rem' }}>
                                    <Typography
                                        sx={{
                                            lineHeight: '1.1',
                                            color: '#919193',
                                            fontSize: '1.2rem',
                                            textTransform: 'uppercase',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {item?.actionType}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        style={{
                                            fontSize: '1.4rem',
                                            color: '#000',
                                            marginLeft: '0.8rem',
                                            fontWeight: '500',
                                            lineHeight: '1.1',
                                            paddingBottom: '0.8rem',
                                        }}
                                    >
                                        {item?.valueFrom}
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
                {/* Label Section */}
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'flex-end',
                    }}
                >
                    <StatusIndicator
                        label={t(
                            item?.selectionRuleTypeID ===
                                CSA_FIELD_TYPES_SELECTION_RULE_TYPE.MULTI_SELECT
                                ? getChangeLabel(item?.multiValueAssignments)
                                : item?.actionType,
                        )}
                        size="small"
                        variant={
                            item?.selectionRuleTypeID ===
                            CSA_FIELD_TYPES_SELECTION_RULE_TYPE.MULTI_SELECT
                                ? getChangeLabelVariant(
                                      item?.multiValueAssignments,
                                  )
                                : item?.actionType === ACTION_TYPES.ADDED
                                ? 'Approved'
                                : item?.actionType === ACTION_TYPES.DELETED
                                ? 'Rejected'
                                : ''
                        }
                        sx={{ fontWeight: '500' }}
                    />
                </Box>
            </Box>
        </Box>
    )
}
